import { notifySessionExpired } from "../auth/mfaStepUp";
import { keysTrustResponse } from "./keysTrustApi";

const BASE = "/admin/keys-trust/parser-signers";
export type ParserSigner = {
  contract_version: "kavoosh-parser-managed-signer.v1";
  operation_id: string; key_record_id: string; fingerprint_sha256: string | null;
  public_key_pem: string | null; lifecycle: "provisioning" | "staged" | "abandoned" | "active" | "retired" | "revoked";
  custody_health: "healthy" | "unavailable"; label: string | null; policy_id: string | null;
  private_material_exposed: false;
};
export type ParserSigningReview = {
  contract_version: "kavoosh-parser-package-signing-review.v1";
  operation_id: string; policy_id: string; publisher_fingerprint: string; publisher_label: string;
  inputs_sha256: string; source_commit: string; release_key: string; release_version: string;
  image_config_digest: string; supported_builds: string[]; limitations: string; review_sha256: string;
};
async function response(path: string, init: RequestInit = {}) {
  try { return await keysTrustResponse(`${BASE}${path}`, init); }
  catch (error) { if (error instanceof Error && error.message === "SESSION_UNAVAILABLE") notifySessionExpired(); throw error; }
}
async function json<T>(path: string, body?: object): Promise<T> {
  return (await response(path, body ? { method: "POST", body: JSON.stringify(body) } : {})).json() as Promise<T>;
}
function signer(value: ParserSigner): ParserSigner {
  if (value.contract_version !== "kavoosh-parser-managed-signer.v1" || value.private_material_exposed !== false ||
      !/^[a-f0-9-]{36}$/.test(value.operation_id) ||
      (value.public_key_pem && (!value.public_key_pem.includes("BEGIN PUBLIC KEY") || value.public_key_pem.includes("PRIVATE"))) ||
      (value.fingerprint_sha256 && !/^[a-f0-9]{64}$/.test(value.fingerprint_sha256))) throw new Error("Invalid public parser signing identity");
  return value;
}
export async function listParserSigners() {
  const values = await json<ParserSigner[]>("");
  if (!Array.isArray(values)) throw new Error("Invalid parser signing identity list");
  return values.map(signer);
}
export const stageParserSigner = async (operationId: string) => signer(await json<ParserSigner>("", { operation_id: operationId }));
export const activateParserSigner = async (item: ParserSigner, label: string, reason: string, previous: string | null) => signer(await json<ParserSigner>(`/${item.operation_id}/activation`, {
  label, reason, expected_fingerprint: item.fingerprint_sha256, replaces_operation_id: previous, confirm_owned_key: true,
}));
export const changeParserSigner = async (item: ParserSigner, action: "retirement" | "revocation", reason: string) => signer(await json<ParserSigner>(`/${item.operation_id}/${action}`, { reason, confirm: true }));
export async function reviewParserInputs(operation: string, file: File) {
  const value = await (await response(`/${operation}/package-review`, { method: "POST", headers: { "Content-Type": "application/octet-stream" }, body: file })).json() as ParserSigningReview;
  if (value.contract_version !== "kavoosh-parser-package-signing-review.v1" || value.operation_id !== operation || !/^[a-f0-9]{64}$/.test(value.review_sha256)) throw new Error("Invalid parser signing review");
  return value;
}
export async function signParserInputs(item: ParserSigner, file: File, review: ParserSigningReview) {
  if (review.operation_id !== item.operation_id || review.policy_id !== item.policy_id || review.publisher_fingerprint !== item.fingerprint_sha256) throw new Error("Parser signing review identity changed");
  const result = await response(`/${item.operation_id}/package?review_sha256=${encodeURIComponent(review.review_sha256)}&confirmed=true`, { method: "POST", headers: { "Content-Type": "application/octet-stream", Accept: "application/octet-stream" }, body: file });
  if (result.headers.get("content-type")?.split(";", 1)[0] !== "application/octet-stream" || result.headers.get("X-Parser-Publisher-Policy-ID") !== item.policy_id) throw new Error("Invalid signed parser package response");
  const blob = await result.blob();
  if (!blob.size || blob.size > 32 * 1024 * 1024) throw new Error("Signed package exceeds its bound");
  return new File([blob], "windows-parser-managed.kavparser", { type: "application/octet-stream" });
}
